import { formatAmountToBigNumber } from './helper'
import { LiquidationAsset, TargetCoinLiquidation, NetworkConfig } from './types'
import { NATIVE_ADDRESS, SupportedNetWork } from './constants'
import { ethers } from 'ethers'
import Web3 from 'web3'
import axios from 'axios'
import BigNumber from 'bignumber.js'
import {
  SwapSide,
  BuildOptions,
  Address,
  PriceString,
  constructPartialSDK,
  constructGetRate,
  constructGetBalances,
  constructGetTokens,
  constructBuildTx,
  constructAxiosFetcher,
  Token,
} from '@paraswap/sdk'
import refcodesData from '../refcodes.json'
import { getRefCode } from '@/utils/localStorage'

// Type definition for refcodes JSON structure
interface RefCodeItem {
  refCode: string
  walletAddress: string
  feeBps: number
  solWalletAddress: string
  suiWalletAddress: string
}

interface RefCodesData {
  refcodes: RefCodeItem[]
  default: {
    walletAddress: string
    partnerFeeBps: number
    solWalletAddress: string
    suiWalletAddress: string
  }
}

const Web3HttpProvider = require('web3-providers-http')

// ERC20 ABI for token interactions
const erc20ABI: any[] = [
  {
    inputs: [{ name: '_owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: 'balance', type: 'uint256' }],
    type: 'function',
  },
  {
    inputs: [
      { name: '_spender', type: 'address' },
      { name: '_value', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
  {
    inputs: [
      { name: '_owner', type: 'address' },
      { name: '_spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
]

// Helper function to get partner info from JSON file
export const getPartnerInfoFromJSON = (
  refCode?: string,
): { walletAddress: string; feeBps: number; solWalletAddress: string, suiWalletAddress: string } => {
  try {
    const data = refcodesData as RefCodesData

    // If refcode provided, try to find it in refcodes array
    if (refCode) {
      const refCodeData = data.refcodes.find(
        (item) => item.refCode.toLowerCase() === refCode.toLowerCase(),
      )
      if (refCodeData) {
        return {
          walletAddress: refCodeData.walletAddress,
          feeBps: refCodeData.feeBps,
          solWalletAddress: refCodeData.solWalletAddress,
          suiWalletAddress: refCodeData.suiWalletAddress
        }
      }
    }

    // If not found or no refcode, use default
    return {
      walletAddress: data.default.walletAddress,
      feeBps: data.default.partnerFeeBps,
      solWalletAddress: data.default.solWalletAddress,
      suiWalletAddress: data.default.suiWalletAddress,
    }
  } catch (error) {
    console.error('Error getting partner info from JSON:', error)
    // Fallback to hardcoded default
    return {
      walletAddress: '0xa4076EE1f379854124a87389443B74D1D3662630',
      feeBps: 0,
      solWalletAddress: '8f1GX2BxENLMuSiUYFKZE7tUig9QSRsP6QvQiwUUL3aY',
      suiWalletAddress: '0x3f11c477531ddd3c497d6cd23f0d04779305685cbd1c952130369a9b3ba9b0c5',
    }
  }
}

// ParaSwap Controller - Real implementation
class ParaSwapController {
  private _paraSwap: any
  private _web3: Web3 = new Web3()
  private _network: any = 56 // Default to BSC
  private _isSend: boolean = false
  private _receiver: string = ''
  private _partnerInfoCache: { walletAddress: string; feeBps: number } | null = null
  private _partnerInfoCacheRefCode: string | null = null

  get network(): any {
    return this._network
  }

  set network(value: any) {
    this._network = value
  }

  get web3(): Web3 {
    return this._web3
  }

  set web3(value: Web3) {
    this._web3 = value
  }

  get paraSwap(): any {
    return this._paraSwap
  }

  set paraSwap(value: any) {
    this._paraSwap = value
  }

  get isSend(): boolean {
    return this._isSend
  }

  set isSend(value: boolean) {
    if (!value) {
      this.receiver = ''
    }
    this._isSend = value
  }

  get receiver(): string {
    return this._receiver
  }

  set receiver(value: string) {
    this._receiver = value
  }

  onChangeNetwork(network: NetworkConfig) {
    // Check if WalletConnect is connected via wagmi
    const ethereum = (window as any).ethereum
    if (ethereum && ethereum.isWalletConnect) {
      try {
        this.web3 = new Web3(ethereum)
        // Test the provider
        this.web3.eth.getChainId().then(chainId => {
        }).catch(error => {
          const mWeb3Provider = new Web3HttpProvider(network?.fallBack ?? network.rpc)
          this.web3 = new Web3(mWeb3Provider)
        })
      } catch (error) {
        const mWeb3Provider = new Web3HttpProvider(network?.fallBack ?? network.rpc)
        this.web3 = new Web3(mWeb3Provider)
      }
    } else {
      // Use RPC provider
      const mWeb3Provider = new Web3HttpProvider(network?.fallBack ?? network.rpc)
      this.web3 = new Web3(mWeb3Provider)
    }

    this.paraSwap = constructPartialSDK(
      {
        chainId: network.chainId,
        fetcher: constructAxiosFetcher(axios),
      },
      constructGetRate,
      constructGetBalances,
      constructGetTokens,
      constructBuildTx,
    )
    this.network = network.chainId
  }

  async getBalanceWeb3(token: LiquidationAsset, userAddress: string) {
    try {
      // Validate token address
      if (!token.address || !userAddress) {
        return '0'
      }

      if (token.address?.toLowerCase() === NATIVE_ADDRESS.toLowerCase()) {
        // Native token balance
        const balance = await this.web3.eth.getBalance(userAddress)
        return balance
      } else {
        // ERC20 token balance
        // Validate address format
        if (!token.address.startsWith('0x') || token.address.length !== 42) {
          return '0'
        }

        const contract = new this.web3.eth.Contract(erc20ABI, token.address)

        // Add timeout and retry logic
        let retries = 2
        let lastError: any = null

        while (retries >= 0) {
          try {
            const balance = await Promise.race([
              contract.methods.balanceOf(userAddress).call({}, 'latest'),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('RPC call timeout')), 10000)
              )
            ]) as any

            // Validate response - balance should be a string or BigNumber
            if (balance === null || balance === undefined) {
              throw new Error('Invalid balance response: null or undefined')
            }

            // Convert to string if it's a BigNumber or number
            const balanceStr = typeof balance === 'string' ? balance : balance.toString()

            // Validate it's a valid number string
            if (balanceStr && (balanceStr === '0' || /^\d+$/.test(balanceStr))) {
              return balanceStr
            }

            throw new Error(`Invalid balance format: ${balanceStr}`)
          } catch (e: any) {
            lastError = e

            console.log('lastError', lastError)
            // If error is about invalid return values (likely RPC/contract issue), skip retries
            if (e?.message?.includes('Returned values aren\'t valid') ||
              e?.message?.includes('Out of Gas') ||
              e?.message?.includes('invalid opcode')) {
              // Don't retry for these types of errors, likely contract/RPC issue
              return '0'
            }

            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1s before retry
            }
            retries--
          }
        }

        // If all retries failed, return '0'
        return '0'
      }
    } catch (e: any) {
      return '0'
    }
  }

  async getAllowanceWeb3(
    account: string,
    spender: string,
    tokenAddress: string,
  ) {
    try {
      // Validate addresses
      if (!account || !spender || !tokenAddress) {
        return '0'
      }

      if (!tokenAddress.startsWith('0x') || tokenAddress.length !== 42) {
        return '0'
      }

      const contract = new this.web3.eth.Contract(erc20ABI, tokenAddress)

      // Add timeout and retry logic
      let retries = 2
      let lastError: any = null

      while (retries >= 0) {
        try {
          const allowance = await Promise.race([
            contract.methods.allowance(account, spender).call({}, 'latest'),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('RPC call timeout')), 10000)
            )
          ]) as any

          // Validate response
          if (allowance === null || allowance === undefined) {
            throw new Error('Invalid allowance response: null or undefined')
          }

          // Convert to string if it's a BigNumber or number
          const allowanceStr = typeof allowance === 'string' ? allowance : allowance.toString()

          // Validate it's a valid number string
          if (allowanceStr && (allowanceStr === '0' || /^\d+$/.test(allowanceStr))) {
            return allowanceStr
          }

          throw new Error(`Invalid allowance format: ${allowanceStr}`)
        } catch (e: any) {
          lastError = e

          console.log('lastError', lastError)
          // If error is about invalid return values (likely RPC/contract issue), skip retries
          if (e?.message?.includes('Returned values aren\'t valid') ||
            e?.message?.includes('Out of Gas') ||
            e?.message?.includes('invalid opcode')) {
            // Don't retry for these types of errors, likely contract/RPC issue
            return '0'
          }

          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1s before retry
          }
          retries--
        }
      }

      // If all retries failed, return '0'
      return '0'
    } catch (e: any) {
      return '0' // Return '0' instead of true
    }
  }

  async getRate(
    from: any,
    to: any,
    amount: PriceString,
    userAddress: string,
    slippage?: number,
  ) {
    try {
      const priceRoute = await this.paraSwap.getRate({
        srcToken: from.address,
        destToken: to.address,
        amount,
        userAddress,
        side: SwapSide.SELL,
        srcDecimals: from.decimals,
        destDecimals: to.decimals,
        slippage,
      })
      return { priceRoute }
    } catch (e: any) {
      return e.response?.data || { error: e.message }
    }
  }

  async buildSwap(
    from: any,
    to: any,
    srcAmount: PriceString,
    destAmount: PriceString,
    userAddress: Address,
  ) {
    try {
      const mPriceRoute: any = await this.getRate(
        from,
        to,
        srcAmount,
        userAddress,
      )

      if (!mPriceRoute.error) {
        // Get refcode from localStorage (which checks URL first, then localStorage)
        const refCode = getRefCode()

        // Get partner info from JSON file (with caching)
        let partnerInfo = this._partnerInfoCache
        if (!partnerInfo || this._partnerInfoCacheRefCode !== refCode) {
          partnerInfo = getPartnerInfoFromJSON(refCode || undefined)

          this._partnerInfoCache = partnerInfo
          this._partnerInfoCacheRefCode = refCode || null
        }

        const options: BuildOptions = {
          ignoreGasEstimate: true,
        }

        const txParams: any = await this.paraSwap.buildTx(
          {
            srcToken: from.address,
            destToken: to.address,
            srcAmount,
            destAmount,
            priceRoute: mPriceRoute.priceRoute,
            userAddress,
            partner: 'SellAll',
            partnerAddress: partnerInfo.walletAddress,
            partnerFeeBps: partnerInfo.feeBps,
            receiver: this.isSend ? this.receiver : userAddress,
            srcDecimals: from.decimals,
            destDecimals: to.decimals,
          },
          options,
        )

        if (txParams.message) {
          throw Error(txParams.message)
        } else {
          const mGas = txParams.gasPrice
          delete txParams.gasPrice
          const _tx = {
            ...txParams,
            chainId: this.web3.utils.toHex(txParams.chainId),
            gasPrice: Math.round(
              new BigNumber(mGas).multipliedBy(new BigNumber('1.2')).toNumber(),
            ).toString(),
          }
          const estimateGas = await this.web3.eth.estimateGas(_tx)
          if (estimateGas) {
            _tx.gas = Math.round(Number(estimateGas * 1.2))
          } else {
            _tx.gas = this.web3.utils.toHex(21000)
          }
          return _tx
        }
      } else {
        throw Error(mPriceRoute.error)
      }
    } catch (e) {
      throw e
    }
  }

  async approve({
    userAddress,
    spender,
    callbackOptions,
    symbol,
    showDialog = true,
    inputTokenAddress,
  }: {
    userAddress: string
    spender: string
    callbackOptions: any
    symbol: string
    showDialog?: boolean
    inputTokenAddress?: string
  }) {
    const { onSuccess, onError, onCancel } = callbackOptions || {}

    try {
      // Check if wallet is connected
      if (!userAddress) {
        throw new Error('No wallet connected')
      }

      // Show confirmation dialog if requested
      if (showDialog) {
        const confirmed = window.confirm(
          `Approve ${symbol}?\n\nThis will allow ParaSwap to access your ${symbol} tokens for trading.`
        )

        if (!confirmed) {
          onCancel?.()
          return false
        }
      }

      const contract = new this.web3.eth.Contract(erc20ABI, inputTokenAddress)

      // Check current allowance first
      const currentAllowance = await contract.methods.allowance(userAddress, spender).call()
      // Check if already approved
      const maxUint256 = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
      const currentAllowanceBN = ethers.BigNumber.from(currentAllowance)
      const maxUint256BN = ethers.BigNumber.from(maxUint256)

      if (currentAllowanceBN.gte(maxUint256BN)) {
        onSuccess?.()
        return true
      }

      // Encode approval transaction
      const abi = await contract.methods
        .approve(spender, maxUint256)
        .encodeABI()

      // Prepare transaction data
      const data = {
        from: userAddress,
        data: abi,
        to: inputTokenAddress,
        value: '0x0',
      }

      const receipt = await this.web3.eth.sendTransaction(data)

      console.log('receipt', receipt)
      onSuccess?.()
      return true

    } catch (e) {
      onError?.(e)
      return false
    }
  }
}

export const paraController = new ParaSwapController()

export const getBestPrice = async ({
  amount,
  inputToken,
  outputToken,
  userAddress,
  onHandleError,
}: {
  amount: string
  inputToken: LiquidationAsset
  outputToken: LiquidationAsset
  userAddress: string
  onHandleError: (field: string, error: string) => void
}) => {
  try {
    const fieldError = `${inputToken.network.chainId}.${inputToken.symbol}`
    const srcAmount = formatAmountToBigNumber(amount, inputToken?.decimals)

    const priceRouteOrError = await paraController.getRate(
      inputToken as any,
      outputToken as any,
      srcAmount.toFixed(0),
      userAddress,
    )

    if ((priceRouteOrError as any).error) {
      if (
        (priceRouteOrError as any).error
          .toString()
          .includes('ESTIMATED_LOSS_GREATER_THAN_MAX_IMPACT')
      ) {
        onHandleError(fieldError, 'highImpact')
      } else {
        onHandleError(fieldError, 'routeNotFound')
      }
      return {}
    }
    const priceRoute = priceRouteOrError?.priceRoute

    const gasPrice = await paraController.web3.eth.getGasPrice()
    const bnbCost = paraController.web3.utils
      .fromWei(
        ethers.BigNumber.from(gasPrice.toString())
          .mul(ethers.BigNumber.from(priceRoute.gasCost))
          .toString(),
        'ether',
      )
      .toString()

    return { priceRoute, bnbCost }
  } catch (error: any) {
    onHandleError(`${inputToken.network.chainId}.${inputToken.symbol}`, error)
    return {}
  }
}

export const getTokenBalance = async ({
  token,
  spender,
  userAddress,
}: {
  token: LiquidationAsset
  userAddress: string
  spender?: string
}) => {
  try {
    // Validate inputs
    if (!token || !userAddress) {
      return {
        allowance: '0',
        balance: '0',
      }
    }

    let allowance = '0' // Initialize as string '0' instead of false

    const balance = await paraController.getBalanceWeb3(token, userAddress)

    // Only check allowance for ERC20 tokens (not native) and if spender is provided
    if (
      token?.address?.toLowerCase() !== NATIVE_ADDRESS.toLowerCase() &&
      spender &&
      token.address &&
      token.address.startsWith('0x') &&
      token.address.length === 42
    ) {
      try {
        const allowanceResult = await paraController.getAllowanceWeb3(
          userAddress,
          spender,
          token.address,
        )
        allowance = allowanceResult || '0'
      } catch (allowanceError: any) {
        allowance = '0' // Default to '0' if allowance check fails
      }
    } else {
      allowance = '0' // Set to '0' for native tokens or when spender is not provided
    }

    return {
      allowance,
      balance: balance || '0',
    }
  } catch (error: any) {
    return {
      allowance: '0',
      balance: '0',
    }
  }
}

export const getNativeBalance = async (userAddress: string) => {
  try {
    const { web3 } = paraController
    if (!userAddress) {
      return 0
    }

    // Add timeout and retry logic
    let retries = 2
    let lastError: any = null

    while (retries >= 0) {
      try {
        const balance = await Promise.race([
          web3.eth.getBalance(userAddress),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('RPC call timeout')), 10000)
          )
        ]) as string

        const balanceFormat = web3.utils.fromWei(balance, 'ether')
        return Number(balanceFormat) || 0
      } catch (e: any) {
        lastError = e
        console.log('lastError', lastError)
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1s before retry
        }
        retries--
      }
    }

    // If all retries failed
    return 0
  } catch (error: any) {
    return 0
  }
}

export const getAllowanceWeb3 = (network: NetworkConfig) => {
  const mWeb3 = new Web3()
  const provider = new Web3HttpProvider(network?.rpc)
  mWeb3.setProvider(provider)
  return mWeb3
}

export const getSourceDestTokenByNetwork = async (
  networkId: any,
  sourceToken: LiquidationAsset,
  targetCoin: TargetCoinLiquidation,
) => {
  paraController.network = networkId

  // Get tokens from ParaSwap API
  const tokensOrError = await paraController.paraSwap.getTokens()

  if (tokensOrError?.message) {
    return { inputToken: sourceToken, outputToken: null }
  }

  let inputToken: any
  let outputToken: any

  // Determine output token symbol based on targetCoin
  let outputTokenSymbol: string | null = null

  if (targetCoin === TargetCoinLiquidation.NATIVE) {
    // Find native token symbol for this network
    const network = SupportedNetWork.find((n: any) => n.chainId === networkId)
    if (network?.nativeCurrency?.symbol) {
      outputTokenSymbol = network.nativeCurrency.symbol
    } else {
      return { inputToken: sourceToken, outputToken: null }
    }
  } else {
    outputTokenSymbol = targetCoin
  }

  tokensOrError.forEach((token: Token) => {
    // Find input token
    if (
      token.symbol?.toLowerCase() === sourceToken.symbol.toLowerCase() &&
      token.address?.toLowerCase() ===
      (sourceToken.address || NATIVE_ADDRESS).toLowerCase()
    ) {
      inputToken = token
    }

    // Find output token (USDC/USDT/NATIVE)
    if (targetCoin === TargetCoinLiquidation.NATIVE) {
      // For native token, match symbol and ensure it's the native address
      if (
        token.symbol?.toLowerCase() === outputTokenSymbol?.toLowerCase() &&
        token.address?.toLowerCase() === NATIVE_ADDRESS.toLowerCase()
      ) {
        outputToken = token
      }
    } else {
      // For USDC/USDT, just match symbol
      if (token.symbol?.toLowerCase() === outputTokenSymbol?.toLowerCase()) {
        outputToken = token
      }
    }
  })

  return {
    inputToken: inputToken
      ? {
        ...sourceToken,
        address: inputToken.address,
        decimals: inputToken.decimals,
      }
      : sourceToken,
    outputToken,
  }
}

export const unlockToken = async ({
  inputToken,
  priceRoute,
  userAddress,
  onSuccess,
  onError,
}: {
  inputToken: LiquidationAsset
  priceRoute
  userAddress: string
  onSuccess?: () => void
  onError?: () => void
}) => {
  const { symbol } = inputToken

  return await paraController.approve({
    userAddress,
    spender: priceRoute?.tokenTransferProxy,
    callbackOptions: {
      onError,
      onSuccess,
    },
    symbol,
    showDialog: false,
    inputTokenAddress: inputToken.address,
  })
}
